import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, ok: false, error: null });

  useEffect(() => {
    if (!token) {
      setState({ loading: false, ok: false, error: "Missing token in the link." });
      return;
    }
    api.post("/auth/verify-email", { token })
      .then((r) => {
        localStorage.setItem("iat_token", r.data.token);
        localStorage.setItem("iat_user", JSON.stringify(r.data.user));
        setState({ loading: false, ok: true, error: null });
        setTimeout(() => nav("/app/upload"), 1400);
      })
      .catch((err) => {
        setState({ loading: false, ok: false, error: err?.response?.data?.detail || "Verification failed" });
      });
  }, [token, nav]);

  return (
    <div className="min-h-screen grid place-items-center px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8" data-testid="verify-logo-link">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-purple-400 to-purple-600 grid place-items-center"><ShieldCheck size={18} className="text-white"/></div>
          <span className="font-semibold text-slate-900 text-lg">Informed <span className="text-purple-600">Accelerator</span></span>
        </Link>
        <Card className="glass rounded-2xl">
          <CardContent className="p-8 text-center">
            {state.loading && (
              <div className="space-y-3" data-testid="verify-loading">
                <Loader2 className="animate-spin mx-auto text-purple-600" size={28} />
                <p className="text-slate-600 text-sm">Confirming your email…</p>
              </div>
            )}
            {!state.loading && state.ok && (
              <div className="space-y-3" data-testid="verify-success">
                <CheckCircle2 className="mx-auto text-emerald-600" size={36} />
                <div className="text-slate-900 font-semibold">Email confirmed</div>
                <p className="text-slate-600 text-sm">Signing you in and redirecting to your dashboard…</p>
              </div>
            )}
            {!state.loading && !state.ok && (
              <div className="space-y-3" data-testid="verify-error">
                <AlertTriangle className="mx-auto text-amber-600" size={36} />
                <div className="text-slate-900 font-semibold">We couldn't verify this link</div>
                <p className="text-slate-600 text-sm">{state.error}</p>
                <div className="flex gap-2 justify-center pt-2">
                  <Link to="/signup"><Button variant="outline" className="rounded-xl">Sign up again</Button></Link>
                  <Link to="/login"><Button className="btn-primary rounded-xl">Back to sign in</Button></Link>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
